import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "store";
import { useWindowDimensions } from "resources/BrowserManager";
import forge from "node-forge";
import strings from "resources/strings";
import ApiManager from "resources/ApiManager/api";
import {
  Flex,
  Space,
  Typography,
  Button,
  Modal,
  Upload,
  Input,
  Tag,
  Tooltip,
  Alert,
  message,
  Steps,
  theme,
} from "antd";
import {
  FileAddOutlined,
  LockFilled,
  Loading3QuartersOutlined,
  InfoCircleFilled,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

import CONSTANTS from "shared/constants";

const { Dragger } = Upload;
const { Text } = Typography;

import { getNowUnix } from "shared/utilities";

import "./style.scss";
const styleWrapperClass = "modalSatCerts";

function ModalSatCerts({ openTitle, flowType }) {
  const { isSmallView } = useWindowDimensions();

  const navigate = useNavigate();

  const { token: theme_token } = theme.useToken();

  const user = useStore((state) => state.user);
  const activeRfc = useStore((state) => state.activeRfc);
  const setActiveRfc = useStore((state) => state.setActiveRfc);
  const updateUserRfc = useStore((state) => state.updateUserRfc);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [cerStatus, setCerStatus] = useState("pending");
  const [keyStatus, setKeyStatus] = useState("pending");
  const [passStatus, setPassStatus] = useState("pending");
  const [rfcInfo, setRfcInfo] = useState({});
  const [passInput, setPassInput] = useState("");

  const showModal = () => {
    setIsModalOpen(true);
  };

  const restartWizard = () => {
    setCerStatus("pending");
    setKeyStatus("pending");
    setPassStatus("pending");
    setPassInput("");
    setWizardStep(0);
  };

  const handleCancel = () => {
    restartWizard();
    setIsModalOpen(false);
  };

  const handleOk = () => {
    restartWizard();
    setIsModalOpen(false);

    if (activeRfc === "onboarding" || activeRfc === "nuevo-rfc") {
      setActiveRfc(rfcInfo.rfc);
      navigate("/cfdis");
    }
  };

  const getUploadUrl = async (rfc, file_type, file) => {
    let apiRes = await ApiManager.getFileUploadLink(
      "user_sat_credentials",
      rfc,
      file_type,
    );

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code !== 2000) {
      message.error(strings.alertUnexpectedError);
      return;
    }

    const { url, fields } = apiRes.data;
    const form_data = new FormData();
    for (const key in fields) {
      form_data.append(key, fields[key]);
    }
    form_data.append("file", file);

    //Important: we use fetch instead of axios here because we need to send a bare bones request to S3 with no added headers or auth, etc.
    let res = await fetch(url, {
      method: "POST",
      body: form_data,
    });

    if (res.status === 204) {
      return true;
    } else {
      message.error(strings.alertUnexpectedError);
      return;
    }
  };

  const handleUploadFile = async (options) => {
    const { onSuccess, onError, file } = options;

    if (file) {
      let file_type = null;
      if (!!!file.type) {
        file_type = file.name;
        file_type = file_type.split(".").pop();
        if (file_type !== "key") {
          setKeyStatus("error");
          return;
        }
        file_type = "application/octet-stream"; //Use a valid file type for .key supported in backend
      } else {
        file_type = file.type;
      }

      if (file_type === "application/x-x509-ca-cert") {
        let reader = new FileReader();
        reader.readAsArrayBuffer(file, "UTF-8");
        reader.onload = async function (evt) {
          let file_content = evt.target.result;

          const cert = forge.pki.certificateFromAsn1(
            forge.asn1.fromDer(
              new forge.util.ByteStringBuffer(file_content),
              false,
            ),
          );

          const fiscal_name = cert.subject.attributes[0].value;
          const rfc = cert.subject.attributes[5].value
            .replace(" ", "")
            .split("/")[0];
          const expiry_unix = Date.parse(cert.validity.notAfter) / 1000;

          if (flowType === "update" && rfc !== activeRfc) {
            setCerStatus("rfc_mismatch");
            return;
          } else if (flowType === "new" && Object.hasOwn(user.rfcs, rfc)) {
            setCerStatus("rfc_exists");
            return;
          } else if (getNowUnix() >= expiry_unix) {
            setCerStatus("expired");
            return;
          } else {
            setCerStatus("loading");
            setRfcInfo({
              rfc: rfc,
              fiscal_name: fiscal_name,
            });
          }

          const response = await getUploadUrl(rfc, file_type, file);
          if (response) {
            setCerStatus("ok");
            setWizardStep(1);
            onSuccess("ok");
          } else {
            handleCancel();
          }
        };
        reader.onerror = function (evt) {
          onError({ evt });
        };
      } else {
        setKeyStatus("loading");
        const response = await getUploadUrl(rfcInfo.rfc, file_type, file);
        if (response) {
          setKeyStatus("ok");
          setWizardStep(2);
          onSuccess("ok");
        }
      }
    }
  };

  const handlePassInputChange = (e) => {
    setPassInput(e.target.value);
  };

  const handlePassInputSubmit = async (e) => {
    setPassStatus("loading");

    let apiRes = await ApiManager.postUserRfcSatActivate(
      rfcInfo.rfc,
      passInput,
    );

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code === 2000) {
      setPassStatus("ok");

      //Update local store
      updateUserRfc(apiRes.data.rfcs[0]);
    } else if (apiRes.response.code === 4001) {
      setPassStatus("error");
      setPassInput("");
    } else if (apiRes.response.code === 4002) {
      setPassStatus("mismatch");
      setPassInput("");
    } else {
      message.error(strings.alertUnexpectedError);
      setPassStatus("pending");
      setPassInput("");
    }
  };

  const steps = [
    {
      title: "Archivo .CER",
      content:
        cerStatus === "pending" ||
        cerStatus === "expired" ||
        cerStatus === "rfc_mismatch" ||
        cerStatus === "rfc_exists" ? (
          <Flex vertical gap={15}>
            {cerStatus === "expired" ? (
              <Alert
                message="¡El certificado seleccionado está expirado! Utiliza uno vigente."
                type="error"
                showIcon
              />
            ) : cerStatus === "rfc_mismatch" ? (
              <Alert
                message="¡El certificado seleccionado no coincide con tu RFC!"
                description={
                  <Text>
                    Asegúrate de usar un certificado para tu RFC. Si deseas dar
                    de alta un RFC nuevo, ve a la lista de tus RFCs y selecciona
                    la opción 'Nuevo RFC'.
                  </Text>
                }
                type="warning"
                showIcon
              />
            ) : cerStatus === "rfc_exists" ? (
              <Alert
                message="¡El certificado seleccionado pertenece a un RFC que ya tienes activo!"
                description={
                  <Text>
                    Asegúrate de usar un certificado para un RFC nuevo. Si
                    deseas actualizar el certificado de uno de tus RFCs
                    existentes, seleccionalo desde la lista de RFCs y ve a la
                    sección de Certificados.
                  </Text>
                }
                type="warning"
                showIcon
              />
            ) : null}
            <Dragger
              multiple={false}
              showUploadList={false}
              accept=".cer"
              customRequest={handleUploadFile}
            >
              <p className="ant-upload-drag-icon">
                <FileAddOutlined />
              </p>
              <p className="ant-upload-text">
                Haz click aquí o desliza tu archivo .CER en este área.
              </p>
              <p className="ant-upload-hint">Sube tu archivo .CER nuevo.</p>
            </Dragger>
          </Flex>
        ) : (
          <div style={{ marginTop: "15px", marginBottom: "20px" }}>
            <Flex justify="center">
              <Loading3QuartersOutlined
                spin
                style={{
                  fontSize: "30px",
                  color: theme_token.colorPrimary,
                }}
              />
            </Flex>
            <Flex justify="center">
              <Text style={{ fontSize: "16px", marginTop: "10px" }}>
                Procesando tu archivo .CER
              </Text>
            </Flex>
          </div>
        ),
    },
    {
      title: "Archivo .KEY",
      content:
        keyStatus === "pending" ? (
          <>
            {rfcInfo ? (
              <Flex
                vertical={isSmallView}
                style={{
                  marginBottom: "10px",
                  fontSize: "15px",
                  fontWeight: "bold",
                }}
                align="center"
              >
                <Space>
                  <Tooltip title="Si no reconoces el RFC o el nombre, verifica que hayas utilizado el Certificado correcto.">
                    <Tag
                      key="status-tag"
                      icon={<InfoCircleFilled />}
                      color={"purple"}
                      style={{ cursor: "help" }}
                    >
                      {rfcInfo.rfc}
                    </Tag>
                  </Tooltip>
                </Space>
                <Text>{rfcInfo.fiscal_name}</Text>
              </Flex>
            ) : null}
            <Dragger
              multiple={false}
              showUploadList={false}
              accept=".key"
              customRequest={handleUploadFile}
            >
              <p className="ant-upload-drag-icon">
                <FileAddOutlined />
              </p>
              <p className="ant-upload-text">
                Haz click aquí o desliza tu archivo .KEY en este área.
              </p>
              <p className="ant-upload-hint">Sube tu archivo .KEY nuevo.</p>
            </Dragger>
          </>
        ) : keyStatus === "error" ? (
          <Alert
            message="¡Error procesando el archivo .KEY!"
            description={
              <Text>
                Asegúrate de usar el archivo .KEY correspondiente a tu
                certificado.
              </Text>
            }
            type="error"
            showIcon
          />
        ) : (
          <div style={{ marginTop: "15px", marginBottom: "20px" }}>
            <Flex justify="center">
              <Loading3QuartersOutlined
                spin
                style={{
                  fontSize: "30px",
                  color: theme_token.colorPrimary,
                }}
              />
            </Flex>
            <Flex justify="center">
              <Text style={{ fontSize: "16px", marginTop: "10px" }}>
                Procesando tu archivo .KEY
              </Text>
            </Flex>
          </div>
        ),
    },
    {
      title: "Contraseña",
      content:
        passStatus === "ok" ? (
          <div style={{ marginTop: "15px", marginBottom: "20px" }}>
            <Flex justify="center">
              <CheckCircleOutlined
                style={{
                  fontSize: "100px",
                  color: theme_token.colorPrimary,
                }}
              />
            </Flex>
            <Flex justify="center">
              <Text
                style={{
                  fontSize: "20px",
                  marginTop: "10px",
                  textAlign: "center",
                }}
              >
                ¡Tu certificado se ha activado correctamente y está listo para
                ser utilizado!
              </Text>
            </Flex>
            <Flex justify="center">
              <Button
                style={{ marginTop: "20px" }}
                type="primary"
                onClick={() => handleOk()}
              >
                {flowType === "update" ? "Terminar" : "Ver mis CFDIs"}
              </Button>
            </Flex>
          </div>
        ) : passStatus === "mismatch" ? (
          <div style={{ marginTop: "15px", marginBottom: "20px" }}>
            <Flex justify="center">
              <CloseCircleOutlined
                style={{
                  fontSize: "100px",
                  color: theme_token.colorError,
                }}
              />
            </Flex>
            <Flex justify="center">
              <Text
                style={{
                  fontSize: "20px",
                  marginTop: "10px",
                  textAlign: "center",
                }}
              >
                ¡El certificado .CER no coincide con la llave .KEY!
                <br />
                Deberás reiniciar el proceso.
              </Text>
            </Flex>
            <Flex justify="center">
              <Button
                danger
                style={{ marginTop: "20px" }}
                onClick={() => handleCancel()}
              >
                Terminar
              </Button>
            </Flex>
          </div>
        ) : (
          <Flex vertical gap={10}>
            <Text>Introduce la contraseña de tu archivo .KEY</Text>
            <Flex gap={10} vertical={isSmallView}>
              <Input.Password
                prefix={<LockFilled />}
                placeholder="Contraseña"
                style={{ width: isSmallView ? null : "300px" }}
                value={passInput}
                onChange={handlePassInputChange}
                disabled={passStatus !== "pending" && passStatus !== "error"}
              />
              <Button
                type="primary"
                disabled={!passInput}
                onClick={handlePassInputSubmit}
                loading={passStatus === "loading"}
              >
                Validar
              </Button>
            </Flex>
            {passStatus === "error" && (
              <Text style={{ color: "red", fontWeight: "bold" }}>
                La contraseña es incorrecta. Verifica que esté escrita
                correctamente.
              </Text>
            )}
          </Flex>
        ),
    },
  ];

  const items = steps.map((item) => ({
    key: item.title,
    title: item.title,
  }));

  return (
    <div className={styleWrapperClass}>
      <Button type="primary" onClick={showModal}>
        {openTitle}
      </Button>
      <Modal
        title={openTitle}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={860}
        destroyOnClose={true}
        footer={null}
        maskClosable={passStatus !== "loading"}
        closable={passStatus !== "loading"}
      >
        <Flex vertical gap={20}>
          <Text>
            En caso de tener un certficado existente, este se reemplazará por el
            nuevo certificado.
          </Text>
          <Steps
            style={{ margin: "10px 0px 10px 0px" }}
            current={wizardStep}
            items={items}
          />

          <div>{steps[wizardStep].content}</div>

          <Flex justify="flex-end">
            {wizardStep > 0 &&
              passStatus !== "ok" &&
              passStatus !== "mismatch" && (
                <Button
                  onClick={() => restartWizard()}
                  disabled={passStatus === "loading"}
                >
                  Reiniciar
                </Button>
              )}
          </Flex>
        </Flex>
      </Modal>
    </div>
  );
}

export default ModalSatCerts;
